import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, MapPin, Calendar as CalIcon, Heart } from "lucide-react";
import { RSVP } from "@/constants/testIds";
import RsvpForm from "@/components/RsvpForm";
import {
  FloralDivider,
  FloralSprig,
  FloralFlank,
} from "@/components/Floral";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUPLE_PHOTO =
  "/images/couple.jpeg";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
};

// ============ Helpers ============
function buildIcs() {
  // All-day event for Wed, 25 Nov 2026 (date TBD for time; will update once formal invite is set)
  const dtStamp =
    new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Suvansh & Hitanksha//Wedding//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    "UID:suvansh-hitanksha-wedding-2026@delhi",
    `DTSTAMP:${dtStamp}`,
    "DTSTART;VALUE=DATE:20261125",
    "DTEND;VALUE=DATE:20261126",
    "SUMMARY:Suvansh & Hitanksha — Wedding",
    "DESCRIPTION:Save the date! Formal invitation with venue and schedule to follow.",
    "LOCATION:Delhi\\, India",
    "STATUS:CONFIRMED",
    "TRANSP:TRANSPARENT",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return ics;
}

function downloadIcs() {
  const blob = new Blob([buildIcs()], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Suvansh-and-Hitanksha-Wedding.ics";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// ============ Component ============
export default function SaveTheDate() {
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/rsvp`, values);
      setSubmitted(data);
      toast.success(
        values.attending === "yes"
          ? "Thank you — we can’t wait to celebrate with you."
          : "Thank you for letting us know. You’ll be missed."
      );
      setTimeout(() => {
        const el = document.getElementById("rsvp");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } catch (e) {
      const msg = e?.response?.data?.detail
        ? typeof e.response.data.detail === "string"
          ? e.response.data.detail
          : "Please check your details and try again."
        : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCalendar = () => {
    try {
      downloadIcs();
      toast.success("Calendar invite downloaded.");
    } catch {
      toast.error("Couldn’t prepare the calendar file.");
    }
  };

  return (
    <div className="bg-ivory text-navy overflow-x-hidden">
      {/* ============ HERO (deep navy + gold) ============ */}
      <section
        className="relative min-h-[100vh] flex flex-col items-center justify-center px-6 py-24 overflow-hidden bg-navy"
        data-testid="hero-section"
      >
        <div aria-hidden className="absolute inset-0 grain-light opacity-60" />

        <div className="relative text-center max-w-3xl z-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="flex justify-center"
          >
            <FloralSprig className="text-gold/80" width={56} />
          </motion.div>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="eyebrow text-gold mt-3"
          >
            Save the date
          </motion.p>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-display text-6xl sm:text-7xl md:text-8xl leading-[0.95] tracking-tight text-cream mt-8 text-balance"
          >
            Suvansh
            <span className="block italic text-gold font-light text-4xl sm:text-5xl md:text-6xl my-3">
              &amp;
            </span>
            Hitanksha
          </motion.h1>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-10 flex justify-center"
          >
            <FloralDivider className="text-gold" width={300} />
          </motion.div>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="font-display text-2xl sm:text-3xl text-cream/90 tracking-wide mt-2"
          >
            Wednesday
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-2 flex items-center justify-center gap-5"
          >
            <FloralFlank
              className="text-gold/80 hidden sm:block"
              width={80}
              style={{ transform: "scaleX(-1)" }}
            />
            <p className="font-display italic text-4xl sm:text-5xl text-goldsoft">
              November 25, 2026
            </p>
            <FloralFlank className="text-gold/80 hidden sm:block" width={80} />
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={5}
            className="flex items-center justify-center gap-3 mt-8 text-cream/70"
          >
            <MapPin className="w-4 h-4 text-gold" strokeWidth={1.5} />
            <span className="eyebrow">Delhi, India</span>
          </motion.div>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={6}
            className="mt-10 font-display italic text-xl sm:text-2xl text-cream/60"
          >
            Formal invite to follow.
          </motion.p>

          {/* CTAs */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={7}
            className="mt-12 flex flex-wrap items-center justify-center gap-3"
          >
            <a
              href="#rsvp"
              data-testid={RSVP.scrollToRsvp}
              className="inline-flex items-center gap-3 bg-gold text-navy px-9 py-4 hover:bg-goldsoft transition-all duration-300 hover:scale-[1.02] font-body text-sm uppercase tracking-[0.25em]"
            >
              Respond <Heart className="w-3.5 h-3.5" strokeWidth={1.6} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ============ COUPLE PHOTO STRIP ============ */}
      <section className="relative bg-navy py-0 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-5xl mx-auto px-6 py-20 md:py-24 grid md:grid-cols-2 gap-12 items-center"
        >
          <div className="relative mx-auto">
            <div className="relative aspect-[4/5] w-full max-w-md overflow-hidden">
              <img
                src={COUPLE_PHOTO}
                alt="Suvansh and Hitanksha"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div
                aria-hidden
                className="absolute -inset-2 border border-gold/40 -z-10 translate-x-3 translate-y-3"
              />
            </div>
          </div>
          <div className="text-center">
            <p className="eyebrow text-gold">A note from us</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-5 leading-snug text-cream text-balance">
              Two families,
              <br />
              <span className="italic text-goldsoft">one story.</span>
            </h2>
            <div className="my-6 flex justify-center">
              <FloralDivider className="text-gold" width={240} />
            </div>
            <p className="text-cream/70 leading-relaxed max-w-md mx-auto">
              We’re putting the final touches on the day. A formal invitation
              with the full schedule, venue details, and a map is on its way.
              In the meantime — please let us know if you’ll be joining us in
              Delhi this November.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mt-8 text-cream/70">
              <div className="flex items-center gap-2">
                <CalIcon className="w-4 h-4 text-gold" strokeWidth={1.5} />
                <span className="font-body text-sm">Wed · 25 Nov 2026</span>
              </div>
              <span className="w-px h-4 bg-gold/40 hidden sm:block" />
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gold" strokeWidth={1.5} />
                <span className="font-body text-sm">Delhi</span>
              </div>
            </div>
          </div>
        </motion.div>
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
      </section>

      {/* ============ RSVP (light cream) ============ */}
      <section id="rsvp" className="py-24 md:py-32 px-6 relative bg-ivory">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="eyebrow text-golddeep">RSVP</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-balance text-navy">
              Kindly respond
              <span className="block italic text-golddeep font-light text-3xl mt-2">
                at your earliest
              </span>
            </h2>
            <div className="mt-5">
              <FloralFlank className="text-gold" width={140} />
            </div>
            <p className="mt-6 text-navy/70 leading-relaxed max-w-md">
              Your response helps us plan a truly memorable celebration. The
              form takes less than a minute.
            </p>
            <div className="hidden md:block mt-10 relative">
              <div className="aspect-[4/5] w-full max-w-sm overflow-hidden">
                <img
                  src={COUPLE_PHOTO}
                  alt="Suvansh and Hitanksha"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-gold/60" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white border border-edge p-8 sm:p-12 rounded-sm shadow-sm"
          >
            {submitted ? (
              <SuccessCard
                rsvp={submitted}
                onReset={() => setSubmitted(null)}
                onAddToCalendar={handleAddToCalendar}
              />
            ) : (
              <RsvpForm
                onSubmit={handleSubmit}
                loading={loading}
                onAddToCalendar={handleAddToCalendar}
              />
            )}
          </motion.div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-16 px-6 border-t border-edge bg-cream/40">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex justify-center text-gold mb-4">
            <FloralSprig width={48} className="text-golddeep" />
          </div>
          <p className="font-display italic text-2xl text-navy/80">
            With love, Verma&rsquo;s &amp; Jain&rsquo;s
          </p>
          <p className="eyebrow text-navy/50 mt-3">
            Delhi · Wed, 25 Nov 2026 · Formal invite to follow
          </p>
        </div>
      </footer>
    </div>
  );
}

function SuccessCard({ rsvp, onReset, onAddToCalendar }) {
  const yes = rsvp.attending === "yes";
  return (
    <div className="text-center py-6" data-testid={RSVP.successCard}>
      <div className="mx-auto w-14 h-14 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center">
        <Check className="w-6 h-6 text-golddeep" strokeWidth={1.5} />
      </div>
      <h3 className="font-display text-3xl mt-6 text-navy">
        {yes ? "We’re thrilled." : "Thank you."}
      </h3>
      <p className="mt-3 text-navy/70 leading-relaxed">
        {yes
          ? `${rsvp.name}, your spot is saved. We can’t wait to celebrate with you on Wednesday, 25 November 2026.`
          : `${rsvp.name}, thank you for letting us know. You’ll be in our hearts.`}
      </p>
      <div className="mt-6 inline-flex flex-col gap-1 text-sm text-navy/60 font-body">
        <span>Phone: {rsvp.phone}</span>
        {yes && (
          <span>
            Bringing a +1:{" "}
            <span className="text-navy">
              {rsvp.plus_one === "yes" ? "Yes" : "No"}
            </span>
          </span>
        )}
      </div>

      {yes && (
        <button
          type="button"
          onClick={onAddToCalendar}
          data-testid="success-add-to-calendar-button"
          className="mt-7 inline-flex items-center gap-2 bg-navy text-cream px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-gold hover:text-navy transition-all"
        >
          <CalIcon className="w-3.5 h-3.5" strokeWidth={1.6} /> Add to calendar
        </button>
      )}

      <div className="mt-6">
        <button
          type="button"
          onClick={onReset}
          data-testid={RSVP.resetButton}
          className="eyebrow text-golddeep hover:text-navy underline-offset-4 hover:underline transition"
        >
          Submit another response
        </button>
      </div>
    </div>
  );
}
